// External review layers (agent design review). A stronger agent reviews a
// shared graph and publishes annotation layers to the site origin:
//   reviews/<sessionId>/index.json      — layer manifest
//   reviews/<sessionId>/<layerId>.json  — the layer (grows as it streams)
// The browser polls the manifest; layers merge into the session (persisted,
// attributed, individually toggleable). Reviewers never mutate the graph —
// they overlay it, like review comments on a commit.

import { bus, renderAll, toast } from "./bus";
import { save } from "./persistence";
import { state, type ReviewEvent, type ReviewLayer } from "./state";

interface LayerMeta { id: string; file: string; author?: string; createdAt?: number; status?: string; seq?: number }

export function mergeReviewLayer(meta: LayerMeta, data: any): void {
  const existing = state.reviews.find((l) => l.id === meta.id);
  const merged: ReviewLayer = {
    id: meta.id,
    author: data.author || meta.author || "reviewer",
    agent: data.agent || "",
    reviewerSession: data.reviewerSession || "",
    createdAt: meta.createdAt || Date.now(),
    status: meta.status || "complete",
    seq: meta.seq || 0,
    events: Array.isArray(data.events) ? data.events : [],
    enabled: existing ? existing.enabled : true,
  };
  if (existing) Object.assign(existing, merged, { enabled: existing.enabled });
  else state.reviews.push(merged);
}

let reviewTimer: ReturnType<typeof setInterval> | null = null;

export async function pollReviews(): Promise<boolean> {
  if (!state.rootId || (typeof document !== "undefined" && document.hidden)) return false;
  let changed = false;
  try {
    const res = await fetch(`reviews/${state.sessionId}/index.json`, { cache: "no-store" });
    if (!res.ok) return false;
    const ix = await res.json();
    for (const meta of ix.layers || []) {
      const seen = state.reviews.find((l) => l.id === meta.id);
      if (seen && seen.seq >= (meta.seq || 0)) continue;
      const rl = await fetch(`reviews/${state.sessionId}/${meta.file}`, { cache: "no-store" });
      if (!rl.ok) continue;
      mergeReviewLayer(meta, await rl.json());
      changed = true;
    }
  } catch { /* offline/local — the feature just stays dormant */ }
  if (changed) {
    save();
    renderAll();
    toast("design review updated — new annotations landed");
  }
  return changed;
}

export function startReviewPolling(): void {
  if (reviewTimer) clearInterval(reviewTimer);
  reviewTimer = setInterval(() => void pollReviews(), 3500);
  void pollReviews();
}

type NoteEvent = Extract<ReviewEvent, { t: "note" }> & { author: string; layerId: string };
type BranchEvent = Extract<ReviewEvent, { t: "branch" }> & { author: string; layerId: string };
type SummaryEvent = Extract<ReviewEvent, { t: "summary" }> & { author: string };

export function reviewNotesFor(nodeId: string): NoteEvent[] {
  return state.reviews.filter((l) => l.enabled)
    .flatMap((l) => l.events.filter((e): e is Extract<ReviewEvent, { t: "note" }> => e.t === "note" && e.nodeId === nodeId)
      .map((e) => ({ ...e, author: l.author, layerId: l.id })));
}

export function reviewBranchesFrom(nodeId: string): BranchEvent[] {
  return state.reviews.filter((l) => l.enabled)
    .flatMap((l) => l.events.filter((e): e is Extract<ReviewEvent, { t: "branch" }> => e.t === "branch" && e.fromNodeId === nodeId)
      .map((e) => ({ ...e, author: l.author, layerId: l.id })));
}

export function reviewSummaries(): SummaryEvent[] {
  return state.reviews.filter((l) => l.enabled)
    .flatMap((l) => l.events.filter((e): e is Extract<ReviewEvent, { t: "summary" }> => e.t === "summary")
      .map((e) => ({ ...e, author: l.author })));
}

export function toggleReviewLayer(id: string): void {
  const l = state.reviews.find((x) => x.id === id);
  if (!l) return;
  l.enabled = !l.enabled;
  save();
  renderAll();
}
